import React, { useReducer } from 'react'
import copy from 'copy-to-clipboard'
import toolbarReducer from './store/Reducer'
import ToolbarService from './Service'
import { get } from 'lodash'
import { toolbarInitialState } from './store/State'
import { downloadBase64AsFile } from 'helpers'
import { IProps } from './Interface'
import SessionManagerService from 'smart-components/session-manager/Service'
import { useServiceState, useNotification } from 'helpers'

import UserResponsesManagerService from 'smart-components/user-responses-manager/Service'
import ValidationDialogService from 'smart-components/validation-button/Service'
import { AUTHENTICATION_SERVICE } from 'config/constants/env'

const toolbarContext = {
  state: {
    ...toolbarInitialState,
    _id: '',
    isComplete: false,
    isMiniProfileComplete: false,
    displayPlaylists: false
  },
  handlers: {
    copyToClipboard: () => {},
    downloadPDF: () => {},
    onLogOff: () => {},
    getPointrSocialMediaButton: () => {},
    onCopySocialButtonMarkup: (_markup: string) => true,
    getSocialMediaButtonMarkup: () => '',
    getPubicProfileUrl: () => '',
    showValidationErrors: () => {},
    onClickSettings: () => {}
  }
}

export type ToolbarContext = typeof toolbarContext

export const ToolbarContextProvider = React.createContext<ToolbarContext>(toolbarContext)

const Provider: React.FC<IProps> = ({ children, user, validation }) => {
  const sessionState = useServiceState(SessionManagerService, false)
  const { emitSuccess, emitError } = useNotification()
  const userResponsesManagerState = useServiceState(UserResponsesManagerService, false)

  const currentUser = user || get(sessionState, 'currentUser', {})
  const _id = get(currentUser, '_id', '')
  const firstName = get(currentUser, 'firstName', '')
  const lastName = get(currentUser, 'lastName', '')

  const { isComplete, isMiniProfileComplete } =
    validation?.total || userResponsesManagerState.userResponses?.validation?.total || {}

  const [state, dispatch] = useReducer(toolbarReducer, toolbarInitialState)

  const onCopyToClipboard = () => {
    emitSuccess('Link copied to ClipBoard!')
  }

  const onDownloadPDFError = (error: string) => {
    emitError(error)
  }

  const onDownloadPDFSuccess = (PDF: string) => {
    downloadBase64AsFile(PDF, 'application/pdf', `${firstName} ${lastName}.pdf`)
  }

  const showValidationErrors = () => {
    ValidationDialogService.setIsDialogOpen('OPEN')
  }

  const onClickSettings = () => {
    SessionManagerService.softLogout()
    window.location.href = `${AUTHENTICATION_SERVICE.WEB_URL}/settings`
  }

  const handlers = {
    onLogOff: () => SessionManagerService.logOff(),
    copyToClipboard: ToolbarService.copyToClipboard(_id, copy, onCopyToClipboard),
    downloadPDF: ToolbarService.downloadPDF(_id, dispatch, onDownloadPDFSuccess, onDownloadPDFError),
    getPointrSocialMediaButton: ToolbarService.getPointrSocialMediaButton(state, dispatch),
    onCopySocialButtonMarkup: ToolbarService.onCopySocialButtonMarkup(copy),
    getSocialMediaButtonMarkup: ToolbarService.getSocialMediaButtonMarkup(_id, firstName, lastName),
    getPubicProfileUrl: () => ToolbarService.getPublicProfileUrl(_id),
    showValidationErrors,
    onClickSettings
  }

  const contextValue = {
    state: {
      ...state,
      _id,
      isComplete,
      isMiniProfileComplete,
      displayPlaylists: (sessionState.currentUser?.playlists?.length || 0) > 0
    },
    handlers
  }

  return <ToolbarContextProvider.Provider value={contextValue}>{children}</ToolbarContextProvider.Provider>
}

export default Provider
